import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, User, BarChart3, LogOut, Tag, CreditCard, Eye, EyeOff } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePartner } from '../contexts/PartnerContext';
import { useStreamerMode } from '../contexts/StreamerModeContext';
import BlurredAmount from './BlurredAmount';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    isAuthenticated, 
    partner, 
 
    logout, 
    showLogoutConfirmation,
    showPartnerModalConfirmation 
  } = usePartner();
  const { isStreamerMode, toggleStreamerMode } = useStreamerMode();

  const handleSteamAuth = () => {
    showPartnerModalConfirmation();
  };

  const handleLogoutClick = () => {
    showLogoutConfirmation();
    setIsMenuOpen(false); // Закрываем мобильное меню
  };

  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount);
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'bronze': return 'text-amber-500';
      case 'silver': return 'text-gray-300';
      case 'gold': return 'text-yellow-400';

      default: return 'text-gray-400';
    }
  };

  const isActivePage = (path: string) => {
    return location.pathname === path;
  };

  return (
    <motion.header 
      className="fixed top-0 left-0 right-0 z-50 tesla-glass"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <motion.div 
            className="flex items-center cursor-pointer"
            whileHover={{ scale: 1.02 }}
            onClick={() => navigate('/')}
          >
            <img src="/app_logo.svg" alt="CSFade" className="h-8" />
          </motion.div>

          {/* Desktop Navigation */}
          {!isAuthenticated ? (
            <></>
          ) : (
            <nav className="hidden md:flex space-x-6">
              <button
                onClick={() => navigate('/dashboard')}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActivePage('/dashboard') 
                    ? 'bg-tesla-red text-white' 
                    : 'text-tesla-gray hover:text-white hover:bg-white/5'
                }`}
              >
                <BarChart3 size={16} />
                <span>Панель</span>
              </button>
              {partner?.tier === 'gold' && (
                <button
                  onClick={() => navigate('/dashboard')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActivePage('/dashboard') 
                      ? 'bg-tesla-red text-white' 
                      : 'text-tesla-gray hover:text-white hover:bg-white/5'
                  }`}
                  title="Рекламные ссылки (только Gold)"
                >
                  <BarChart3 size={16} />
                  <span>Рекламные ссылки</span>
                </button>
              )}
              <button
                onClick={() => navigate('/promocodes')}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActivePage('/promocodes') 
                    ? 'bg-tesla-red text-white' 
                    : 'text-tesla-gray hover:text-white hover:bg-white/5'
                }`}
              >
                <Tag size={16} />
                <span>Промокоды</span>
              </button>
            </nav>
          )}

          {/* Auth Section */}
          <div className="hidden md:flex items-center space-x-4">
            {!isAuthenticated ? (
              <button 
                onClick={handleSteamAuth}
                className="tesla-button flex items-center space-x-2"
              >
                <User size={16} />
                <span>Войти</span>
              </button>
            ) : (
              <>
                {/* Partner Info */}
                <div className="flex items-center space-x-4">
                  {/* Balance */}
                  {partner && (
                    <div 
                      className={`text-right cursor-pointer group`}
                      onClick={() => navigate('/payments')}
                    >
                      <p className="text-xs text-tesla-gray uppercase tracking-wider group-hover:text-white transition-colors">Баланс</p>
                      <div className="flex items-center space-x-2 group-hover:text-blue-400 transition-colors">
                        <BlurredAmount 
                          amount={partner.balance || '0'}
                          className="text-white font-semibold"
                          formatCurrency={true}
                        />
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-70 group-hover:opacity-100 transition-opacity">
                          <path d="M20 9.03059V10.9694C20 11.4871 19.56 11.9106 19 11.9294H17.04C15.96 11.9294 14.97 11.1859 14.88 10.1694C14.82 9.57647 15.06 9.02118 15.48 8.63529C15.85 8.27765 16.36 8.07054 16.92 8.07054H19C19.56 8.08936 20 8.51294 20 9.03059Z" fill="currentColor"/>
                          <path d="M18.4701 13.3412H17.0401C15.1401 13.3412 13.5401 11.9953 13.3801 10.2824C13.2901 9.30353 13.6701 8.32471 14.4301 7.62823C15.0701 7.00706 15.9601 6.65882 16.9201 6.65882H18.4701C18.7601 6.65882 19.0001 6.43294 18.9701 6.16C18.7501 3.87294 17.1401 2.31059 14.7501 2.04706C14.5101 2.00941 14.2601 2 14.0001 2H5.00001C4.72001 2 4.45001 2.01882 4.19001 2.05647C1.64001 2.35765 0 4.14588 0 6.70588V13.2941C0 15.8918 2.24001 18 5.00001 18H14.0001C16.8001 18 18.7301 16.3529 18.9701 13.84C19.0001 13.5671 18.7601 13.3412 18.4701 13.3412ZM11.0001 7.88235H5.00001C4.59001 7.88235 4.25001 7.56235 4.25001 7.17647C4.25001 6.79059 4.59001 6.47059 5.00001 6.47059H11.0001C11.4101 7.88235 11.0001 7.88235Z" fill="currentColor"/>
                        </svg>
                      </div>
                    </div>
                  )}
                  
                  {/* Partner Info */}
                  <div className="flex items-center space-x-3">
                    {/* Avatar */}
                    <div className="w-8 h-8 bg-gradient-to-br from-tesla-gray to-gray-900 rounded-full flex items-center justify-center">
                      <User size={16} className="text-white" />
                    </div>
                    
                    {/* Name and Tier */}
                    <div>
                      <p className="text-white text-sm font-medium">{partner?.name}</p>
                      <p className={`text-xs uppercase tracking-wider ${getTierColor(partner?.tier || '')}`}>
                        {partner?.tier}
                      </p>
                    </div>
                  </div>

                  {/* Streamer Mode Button */}
                  <button 
                    onClick={toggleStreamerMode}
                    className={`transition-colors p-2 ${
                      isStreamerMode 
                        ? 'text-blue-400 hover:text-blue-300' 
                        : 'text-tesla-gray hover:text-white'
                    }`}
                    title={isStreamerMode ? 'Выключить режим стримера' : 'Включить режим стримера'}
                  >
                    {isStreamerMode ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>

                  {/* Logout Button */}
                  <button 
                    onClick={handleLogoutClick}
                    className="text-tesla-gray hover:text-white transition-colors p-2"
                    title="Выйти"
                  >
                    <LogOut size={18} />
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white hover:text-tesla-gray transition-colors duration-200"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <motion.div 
            className="md:hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-4 pt-4 pb-6 space-y-4 bg-tesla-black border-t border-white/10">
              {!isAuthenticated ? (
                <>
                  <a href="#features" className="block text-tesla-gray hover:text-white transition-colors duration-200 text-sm uppercase tracking-wider font-medium">
                    Функции
                  </a>
                  <a href="#statistics" className="block text-tesla-gray hover:text-white transition-colors duration-200 text-sm uppercase tracking-wider font-medium">
                    Цифры
                  </a>
                  <a href="#how-it-works" className="block text-tesla-gray hover:text-white transition-colors duration-200 text-sm uppercase tracking-wider font-medium">
                    Как работает
                  </a>
                  <div className="pt-4 space-y-3">
                    <button 
                      onClick={handleSteamAuth}
                      className="w-full tesla-button flex items-center justify-center space-x-2"
                    >
                      <User size={16} />
                      <span>Войти</span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Mobile Partner Info */}
                  <div className="flex items-center space-x-3 py-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-tesla-gray to-gray-900 rounded-full flex items-center justify-center">
                      <User size={20} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{partner?.name}</p>
                      <p className={`text-xs uppercase tracking-wider ${getTierColor(partner?.tier || '')}`}>
                        {partner?.tier}
                      </p>
                    </div>
                  </div>
                  
                  {/* Mobile Balance */}
                  {partner && (
                    <div 
                      className={`py-2 border-t border-white/10 cursor-pointer`}
                      onClick={() => {
                        navigate('/payments');
                        setIsMenuOpen(false);
                      }}
                    >
                      <p className="text-xs text-tesla-gray uppercase tracking-wider">Баланс</p>
                      <div className="flex items-center space-x-2">
                        <BlurredAmount 
                          amount={partner.balance || '0'}
                          className="text-white font-semibold text-lg"
                          formatCurrency={true}
                        />
                      </div>
                    </div>
                  )}

                  {/* Mobile Navigation Links */}
                  <div className="space-y-2 pt-2 border-t border-white/10">
                    <button
                      onClick={() => {
                        navigate('/dashboard');
                        setIsMenuOpen(false);
                      }}
                      className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left font-medium transition-colors ${
                        isActivePage('/dashboard') 
                          ? 'bg-tesla-red text-white' 
                          : 'text-tesla-gray hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <BarChart3 size={18} />
                      <span>Панель</span>
                    </button>
                      <button
                        onClick={() => {
                          navigate('/payments');
                          setIsMenuOpen(false);
                        }}
                        className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left font-medium transition-colors ${
                          isActivePage('/payments') 
                            ? 'bg-tesla-red text-white' 
                            : 'text-tesla-gray hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <CreditCard size={18} />
                        <span>Выплаты</span>
                      </button>
                    {partner?.tier === 'gold' && (
                      <button
                        onClick={() => {
                          navigate('/dashboard');
                          setIsMenuOpen(false);
                        }}
                        className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left font-medium transition-colors ${
                          isActivePage('/dashboard') 
                            ? 'bg-tesla-red text-white' 
                            : 'text-tesla-gray hover:text-white hover:bg-white/5'
                        }`}
                        title="Рекламные ссылки (только Gold)"
                      >
                        <BarChart3 size={18} />
                        <span>Рекламные ссылки</span>
                      </button>
                    )}
                    <button
                      onClick={() => {
                        navigate('/promocodes');
                        setIsMenuOpen(false);
                      }}
                      className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left font-medium transition-colors ${
                        isActivePage('/promocodes') 
                          ? 'bg-tesla-red text-white' 
                          : 'text-tesla-gray hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <Tag size={18} />
                      <span>Промокоды</span>
                    </button>
                  </div>
                  
                  {/* Mobile Actions */}
                  <div className="pt-4 border-t border-white/10 space-y-2">
                    {/* Streamer Mode Toggle */}
                    <button 
                      onClick={toggleStreamerMode}
                      className={`w-full flex items-center justify-center space-x-2 py-3 transition-colors ${
                        isStreamerMode 
                          ? 'text-blue-400 hover:text-blue-300' 
                          : 'text-tesla-gray hover:text-white'
                      }`}
                    >
                      {isStreamerMode ? <EyeOff size={18} /> : <Eye size={18} />}
                      <span>{isStreamerMode ? 'Выключить режим стримера' : 'Включить режим стримера'}</span>
                    </button>
                    
                    {/* Logout */}
                    <button 
                      onClick={handleLogoutClick}
                      className="w-full flex items-center justify-center space-x-2 py-3 text-tesla-gray hover:text-white transition-colors"
                    >
                      <LogOut size={18} />
                      <span>Выйти</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

export default Header;